import React, { useEffect, useState } from 'react';
import { array, func, number, string } from 'prop-types';

import { useFormik } from 'formik';
import * as Yup from 'yup';

import { get, isEmpty } from 'lodash';

import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import GoogleLogo from '../../../components/GoogleLogo';

import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

import classes from '../CreateEntityModal.module.css';

const ValidationSchema = Yup.object().shape({
    technicianAddress: Yup.string().required(),
    skillTypes:        Yup.string().required(),
    skillLevel:        Yup.string().required(),
    isSameAddress:     Yup.boolean()
});

const DEFAULT_FORM_VALUES = {
    technicianAddress: '',
    skillTypes:        '',
    skillLevel:        '',
    isSameAddress:     false
};

const TechnicianForm = ({
    formattedSeniorityLevels,
    formattedSpecialties,
    index,
    onSave,
    skillLevel,
    skillTypes,
    selectedUser,
    technicianData,
    userFormattedAddress,
    ...props
}) => {
    const [technicianAddress, setTechnicianAddress] = useState({});
    // eslint-disable-next-line no-unused-vars
    const [technicianAddress1, setTechnicianAddress1] = useState({});
    const [technicianCity, setTechnicianCity] = useState('');
    const [technicianState, setTechnicianState] = useState('');
    const [technicianZip, setTechnicianZip] = useState('');
    const [initialValues, setInitialValues] = useState({...DEFAULT_FORM_VALUES});

    const formik = useFormik({
        initialValues:      initialValues,
        validationSchema:   ValidationSchema,
        enableReinitialize: true,
        onSubmit:           async (values, { setSubmitting }) => {
            values.startingAddress = technicianAddress;
            onSave(values, index);
        }
    });
    
    useEffect(() => {
        const init = async () => {
            const { userAttributes } = technicianData;
            let formvalues = {
                technicianAddress: userAttributes.startingAddress.formattedAddress,
                skillTypes:        userAttributes.skillTypes,
                skillLevel:        userAttributes.skillLevel,
                isSameAddress:     userAttributes.isSameAddress
            };

            setInitialValues(formvalues);

            if (formvalues.technicianAddress) {
                handleTechnicianGeocodeAddress(formvalues.technicianAddress);
            }
        };

        if ( !isEmpty(technicianData) && !isEmpty(technicianData.userAttributes) ) init();
    }, [technicianData]);

    const handleTechnicianGeocodeAddress = async address => {
        try {
            const results = await geocodeByAddress(address);
            const latLng = await getLatLng(results[0]);

            const streetNumber = results[0].address_components.find(result => {
                return result.types.includes('street_number');
            });

            const route = results[0].address_components.find(result => {
                return result.types.includes('route');
            });

            const city = results[0].address_components.find(result => {
                return result.types.includes('locality');
            });

            const administrativeAreaLevel2 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_2');
            });

            const administrativeAreaLevel3 = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_3');
            });

            const state = results[0].address_components.find(result => {
                return result.types.includes('administrative_area_level_1');
            });

            const zip = results[0].address_components.find(result => {
                return result.types.includes('postal_code');
            });

            const country = results[0].address_components.find(result => {
                return result.types.includes('country');
            });

            const address1Check = streetNumber ? `${streetNumber.long_name} ${route.long_name}` : route.long_name;
            const cityCheck = city ? city.long_name : administrativeAreaLevel3.long_name;

            formik.setFieldValue('technicianAddress', results[0].formatted_address);

            setTechnicianAddress1(address1Check);
            setTechnicianCity(cityCheck);
            setTechnicianState(state.short_name);
            setTechnicianZip(zip.long_name);

            setTechnicianAddress({
                latitude:                 get(latLng, 'lat', ''),
                longitude:                get(latLng, 'lng', ''),
                administrativeAreaLevel1: get(state, 'short_name', ''),
                administrativeAreaLevel2: get(administrativeAreaLevel2, 'long_name', ''),
                country:                  get(country, 'short_name', ''),
                locality:                 cityCheck,
                postalCode:               get(zip, 'long_name', ''),
                route:                    get(route, 'long_name', ''),
                streetNumber:             streetNumber ? streetNumber.long_name : '',
                formattedAddress:         get(results[0], 'formatted_address', '')
            });
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('Error: ', error);
        }
    };

    const onSameAddressClick = () => {
        const isSelected = !formik.values.isSameAddress;
        formik.setFieldValue('isSameAddress', isSelected);
        if (isSelected) {
            if (userFormattedAddress) handleTechnicianGeocodeAddress(userFormattedAddress);
        } else {
            formik.setFieldValue('technicianAddress', '');

            setTechnicianAddress1('');
            setTechnicianCity('');
            setTechnicianState('');
            setTechnicianZip('');

            setTechnicianAddress({});
        }
    };
    
    return (
        <div className={classes.technicianForm}>
            {/* <p className={classes.technicianAttributeText}>Technician Attribute</p> */}
            <div className={classes.inputs}>
                <div className={`${classes.formRow} ${classes.userTypeFormRow}`}>
                    <FormControl variant="outlined" size="small" className={`${classes.inputWrapperHalf} ${classes.technicianAddressField}`}>
                        <InputLabel
                            htmlFor="skillLevel"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocuse
                            }}
                            error={Boolean(formik.errors.skillLevel) && Boolean(formik.touched.skillLevel)}
                        >
                            Skill Level<span className={classes.required}> *</span>
                        </InputLabel>
                        <Select
                            value={formik.values.skillLevel}
                            onChange={ e => {
                                formik.handleChange(e);
                            }}
                            className={classes.select}
                            inputProps={{
                                name:   'skillLevel',
                                id:     'skillLevel',
                                onBlur: formik.handleBlur
                            }}
                            error={Boolean(formik.errors.skillLevel) && Boolean(formik.touched.skillLevel)}
                        >
                            {
                                skillLevel.map(type =>
                                    <MenuItem
                                        key={type.id}
                                        value={type.id}
                                    >
                                        {type.name}
                                    </MenuItem>
                                )
                            }
                        </Select>
                        {formik.touched.skillLevel &&
                                formik.errors.skillLevel && <FormHelperText className={classes.error}>{formik.errors.skillLevel}</FormHelperText>}
                    </FormControl>
                    <FormControl variant="outlined" size="small" className={`${classes.inputWrapperHalf} ${classes.technicianAddressField}`}>
                        <InputLabel
                            htmlFor="skillTypes"
                            classes={{
                                error:   classes.error,
                                focused: classes.labelFocuse
                            }}
                            error={Boolean(formik.errors.skillTypes) && Boolean(formik.touched.skillTypes)}
                        >
                            Skill Type<span className={classes.required}> *</span>
                        </InputLabel>
                        <Select
                            value={formik.values.skillTypes}
                            onChange={ e => {
                                formik.handleChange(e);
                            }}
                            className={classes.select}
                            inputProps={{
                                name:   'skillTypes',
                                id:     'skillTypes',
                                onBlur: formik.handleBlur
                            }}
                            error={Boolean(formik.errors.skillTypes) && Boolean(formik.touched.skillTypes)}
                        >
                            {
                                skillTypes.map(type =>
                                    <MenuItem
                                        key={type.id}
                                        value={type.id}
                                    >
                                        {type.name}
                                    </MenuItem>
                                )
                            }
                        </Select>
                        {formik.touched.skillTypes &&
                                formik.errors.skillTypes && <FormHelperText className={classes.error}>{formik.errors.skillTypes}</FormHelperText>}
                    </FormControl>
                    <FormControl className={classes.inputWrapper}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    onClick={onSameAddressClick}
                                    checked={formik.values.isSameAddress}
                                    name={'isSameAddress'}
                                />
                            }
                            label={'Use Same Address'}
                        />
                    </FormControl>
                </div>
                <div className={`${classes.formRow} ${classes.userTypeFormRow}`}>
                    <PlacesAutocomplete
                        value={formik.values.technicianAddress}
                        onChange={address => formik.setFieldValue('technicianAddress', address)}
                        onSelect={(handleTechnicianGeocodeAddress)}
                    >
                        {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                            <React.Fragment>
                                <FormControl variant="outlined" size="small" className={`${classes.inputWrapperHalf} ${classes.technicianAddressField}`}>
                                    <TextField
                                        {...getInputProps({
                                            name: 'technicianAddress',
                                            id:   'technicianAddress'
                                            
                                        })}
                                        InputProps={{
                                            onBlur:  formik.handleBlur,
                                            classes: {
                                                root: classes.input
                                            }
                                        }}
                                        placeholder="Enter"
                                        variant="outlined"
                                        size="small"
                                        label={<span>Address<span className={classes.required}> *</span></span>}
                                        classes={{
                                            root: classes.input
                                        }}
                                        error={Boolean(formik.errors.technicianAddress) && Boolean(formik.touched.technicianAddress)}
                                    />
                                    {formik.touched.technicianAddress &&
                                        formik.errors.technicianAddress && <FormHelperText className={classes.error}>{formik.errors.technicianAddress}</FormHelperText>}
                                </FormControl>
                                {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                    <List>
                                        {suggestions.map((suggestion, index) => {
                                            return (
                                                <div key={index}>
                                                    <ListItem
                                                        button
                                                        className={classes.autoCompleteListItem}
                                                        {...getSuggestionItemProps(suggestion)}
                                                    >
                                                        <ListItemText primary={suggestion.description} />
                                                    </ListItem>
                                                </div>
                                            );
                                        })}
                                    </List>
                                    <GoogleLogo />
                                </Paper>}
                            </React.Fragment>
                        )}
                    </PlacesAutocomplete>
                    {/* <PlacesAutocomplete
                        value={formik.values.technicianAddress}
                        onChange={address => formik.setFieldValue('technicianAddress', address)}
                        onSelect={(handleTechnicianGeocodeAddress)}
                    >
                        {({ getInputProps, suggestions, getSuggestionItemProps }) => (
                            <React.Fragment>
                                <FormControl className={`${classes.inputWrapperHalf} ${classes.technicianAddressField}`}>
                                    <InputLabel
                                        htmlFor="technicianAddress"
                                        classes={{
                                            error:   classes.error,
                                            focused: classes.labelFocused,

                                        }}
                                        error={Boolean(formik.errors.technicianAddress) && Boolean(formik.touched.technicianAddress)}
                                    >
                                        Address<span className={classes.required}> *</span>
                                    </InputLabel>
                                    <Input
                                        {...getInputProps({
                                            name: 'technicianAddress',
                                            id:   'technicianAddress'
                                        })}
                                        classes={{
                                            error:     classes.cssErrorUnderline,
,

                                        }}
                                        error={Boolean(formik.errors.technicianAddress) && Boolean(formik.touched.technicianAddress)}
                                    />
                                    {formik.touched.technicianAddress &&
                                         formik.errors.technicianAddress && <FormHelperText className={classes.error}>{formik.errors.technicianAddress}</FormHelperText>}
                                </FormControl>
                                {suggestions.length > 0 && <Paper className={classes.autoCompleteList}>
                                    <List>
                                        {suggestions.map((suggestion, index) => {
                                            return (
                                                <div key={index}>
                                                    <ListItem
                                                        button
                                                        className={classes.autoCompleteListItem}
                                                        {...getSuggestionItemProps(suggestion)}
                                                    >
                                                        <ListItemText primary={suggestion.description} />
                                                    </ListItem>
                                                </div>
                                            );
                                        })}
                                    </List>
                                    <GoogleLogo />
                                </Paper>}
                            </React.Fragment>
                        )}
                    </PlacesAutocomplete> */}
                    <FormControl className={`${classes.cityInputWrapper} ${classes.technicianAddressField}`}>
                        <TextField
                            id="city"
                            name="city"
                            type="text"
                            value={technicianCity}
                            label="City"
                            disabled
                            placeholder="Enter"
                            size="small"
                            variant="outlined"
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error: classes.cssErrorUnderline
                                }
                            }}
                        />
                    </FormControl>
                    <FormControl className={`${classes.addressInputWrapper} ${classes.technicianAddressField}`}>
                        <TextField
                            id="state"
                            name="state"
                            type="text"
                            value={technicianState}
                            label="State"
                            disabled
                            placeholder="Enter"
                            size="small"
                            variant="outlined"
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error: classes.cssErrorUnderline
                                }
                            }}
                        />
                    </FormControl>
                    <FormControl className={`${classes.addressInputWrapper} ${classes.technicianAddressField}`}>
                        <TextField
                            id="zip"
                            name="zip"
                            type="text"
                            value={technicianZip}
                            label="Zip Code"
                            disabled
                            placeholder="Enter"
                            size="small"
                            variant="outlined"
                            InputLabelProps={{
                                classes: {
                                    error:   classes.error,
                                    focused: classes.labelFocused
                                }
                            }}
                            InputProps={{
                                classes: {
                                    error: classes.cssErrorUnderline
                                }
                            }}
                        />
                    </FormControl>
                    <Button
                        className={`${classes.Button} ${classes.technicianSavebutton}`}
                        variant="contained"
                        onClick={formik.handleSubmit}
                    >
                        Save
                    </Button>
                </div>
            </div>
        </div>
    );
};

TechnicianForm.propTypes = {
    onSave:                   func,
    index:                    number,
    formattedSeniorityLevels: array,
    formattedSpecialties:     array,
    skillLevel:               array,
    skillTypes:               array,
    userFormattedAddress:     string,
};

export default TechnicianForm;
